"use client"

import type { ReactNode } from "react"
import { useId, useState } from "react"
import { useTranslations } from "next-intl"
import {
  Lightbulb,
  Info,
  AlertTriangle,
  OctagonAlert,
  FlaskConical,
  MessageSquare,
  Quote,
  Zap,
  Sparkles,
} from "lucide-react"
import type { DirectiveSegment } from "@/features/blog/lib/directive-parser"

const labelClass =
  "mb-3 text-[11px] font-bold uppercase tracking-[0.12em]"
const ahrefsBodyClass = "text-[18px] leading-[29px] text-[#333333]"
const ahrefsMutedClass = "text-[16px] leading-[22px] text-[#333333]/70"
const ahrefsBoxClass = "my-[26px] bg-[#fcf7f8] py-[30px] pl-[30px] pr-10"
const ahrefsTableClass = "my-[60px] w-full border-collapse text-[18px] leading-[29px] text-[#333333]"
const ahrefsCellClass = "border border-[#333333] p-[9px] align-top"

const CALLOUT_TONES: Record<string, { bg: string; text: string; iconBg: string }> = {
  green: { bg: "#f4fbf5", text: "#15803d", iconBg: "#e3f6e8" },
  blue: { bg: "#f3f8ff", text: "#1d4ed8", iconBg: "#e2efff" },
  gray: { bg: "#f6f6f7", text: "#5f6670", iconBg: "#eceef1" },
  amber: { bg: "#fff8eb", text: "#b45309", iconBg: "#ffedc2" },
  red: { bg: "#fff4f4", text: "#b91c1c", iconBg: "#ffe0e0" },
  violet: { bg: "#f8f4ff", text: "#6d28d9", iconBg: "#efe5ff" },
}

/* Re-export types and splitBodyIntoSegments for consumers that still import from this file.
   The implementation lives in directive-parser.ts (no "use client") so server components
   can also import directly from there without hitting the client boundary. */
export type { DirectiveSegment, BodySegment } from "@/features/blog/lib/directive-parser"
import { sanitizeHref } from "@/features/blog/lib/sanitize-href"
export { splitBodyIntoSegments } from "@/features/blog/lib/directive-parser"

function parseAttrs(attrStr: string): Record<string, string> {
  const attrs: Record<string, string> = {}
  const re = /([\w-]+)="([^"]*)"/g
  let m: RegExpExecArray | null
  while ((m = re.exec(attrStr)) !== null) {
    attrs[m[1]] = m[2]
  }
  return attrs
}

/* ── Inline markdown renderer (bold + links + italic, for directive bodies) ── */

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = []
  const re = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(\[([^\]]+)\]\(([^)]+)\))/g
  let lastIndex = 0
  let m: RegExpExecArray | null
  let idx = 0

  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index))

    if (m[1]) {
      parts.push(
        <strong key={`${keyPrefix}-b-${idx++}`} className="font-semibold text-[#333333]">
          {m[2]}
        </strong>
      )
    } else if (m[3]) {
      parts.push(
        <em key={`${keyPrefix}-i-${idx++}`} className="italic">
          {m[4]}
        </em>
      )
    } else if (m[5]) {
      const href = sanitizeHref(m[7])
      const isExternal = /^https?:\/\//i.test(href)
      parts.push(
        <a
          key={`${keyPrefix}-a-${idx++}`}
          href={href}
          className="blog-link"
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {m[6]}
        </a>
      )
    }

    lastIndex = m.index + m[0].length
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return parts.length > 0 ? parts : [text]
}

/* ── Body text renderer for simple callout bodies ── */

function renderSimpleBody(body: string, keyPrefix: string): ReactNode {
  if (!body.trim()) return null
  const lines = body.split("\n").filter((l) => l.trim())
  return (
    <>
      {lines.map((line, i) => (
        <p
          key={`${keyPrefix}-p-${i}`}
          className="mb-[15px] text-[18px] leading-[29px] text-[#333333] last:mb-0"
        >
          {renderInline(line, `${keyPrefix}-${i}`)}
        </p>
      ))}
    </>
  )
}

function EditorialCallout({
  label,
  body,
  icon: Icon,
  tone = "blue",
  id,
}: {
  label: string
  body: string
  icon: typeof Info
  tone?: string
  id: string
}) {
  const color = CALLOUT_TONES[tone] ?? CALLOUT_TONES.blue
  return (
    <aside
      className="my-[26px] py-[30px] pl-[30px] pr-10"
      style={{ backgroundColor: color.bg }}
    >
      <div className="mb-[15px] flex items-center gap-3">
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: color.iconBg }}
        >
          <Icon className="size-[15px]" style={{ color: color.text }} />
        </span>
        <p className="m-0 text-[20px] font-bold leading-6 text-[#333333]">
          {label}
        </p>
      </div>
      {renderSimpleBody(body, id)}
    </aside>
  )
}

/* ════════════════════════════════════════════════════════════
   A. EXISTING DIRECTIVES (14 from PR #611)
   ════════════════════════════════════════════════════════════ */

function TipBlock({ body }: { body: string }) {
  return <EditorialCallout label="Pro tip" body={body} icon={Lightbulb} tone="green" id="tip" />
}

function NoteBlock({ body }: { body: string }) {
  return <EditorialCallout label="Note" body={body} icon={Info} tone="blue" id="note" />
}

function SidenoteBlock({ body }: { body: string }) {
  return (
    <aside className={`my-[22px] ${ahrefsMutedClass}`}>
      <span className="inline-flex items-center gap-1.5 font-bold">
        <MessageSquare className="size-[14px]" />
        Sidenote.
      </span>{" "}
      {renderInline(body.replace(/\n+/g, " ").trim(), "sidenote")}
    </aside>
  )
}

function EditorBlock({ body }: { body: string }) {
  return <EditorialCallout label="Editor's Note" body={body} icon={Info} tone="gray" id="editor" />
}

function NutshellBlock({ body }: { body: string }) {
  return <EditorialCallout label="In a nutshell" body={body} icon={Zap} tone="blue" id="nutshell" />
}

function MethodologyBlock({ body }: { body: string }) {
  return <EditorialCallout label="Methodology" body={body} icon={FlaskConical} tone="violet" id="methodology" />
}

function KeyTakeawaysEditorialBlock({ body }: { body: string }) {
  const lines = body.split("\n").filter((l) => l.trim())
  return (
    <aside className="my-[29px]">
      <p className="mb-[15px] text-[20px] font-bold leading-6 text-[#333333]">Key takeaways</p>
      <ul className="list-disc pl-6">
        {lines.map((line, i) => (
          <li key={i} className="pt-0.5 text-[18px] leading-[29px] text-[#333333]">
            <span>{renderInline(line.replace(/^- /, ""), `kt-clean-${i}`)}</span>
          </li>
        ))}
      </ul>
    </aside>
  )
}

function StatBlock({ attrs }: { attrs: Record<string, string> }) {
  const { value, label, source } = attrs
  if (!value) return null
  return (
    <div className={ahrefsBoxClass}>
      <p className="mb-[15px] text-[38px] font-bold leading-[42px] text-[#333333]">
        {value}
      </p>
      {label && (
        <p className={ahrefsBodyClass}>{label}</p>
      )}
      {source && (
        <p className={`mt-[15px] ${ahrefsMutedClass}`}>Source: {source}</p>
      )}
    </div>
  )
}

function StatGroupBlock({ body }: { body: string }) {
  const re = /^:::stat([ \t].+)?$/gm
  const stats: Array<{ value: string; label?: string; source?: string }> = []
  let m: RegExpExecArray | null

  while ((m = re.exec(body)) !== null) {
    const a = parseAttrs((m[1] ?? "").trim())
    if (a.value) {
      stats.push({ value: a.value, label: a.label, source: a.source })
    }
  }

  if (stats.length === 0) return null

  return (
    <div
      className={`${ahrefsBoxClass} grid gap-6`}
      style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 3)}, minmax(0, 1fr))` }}
    >
      {stats.map((s, i) => (
        <div key={i} className="text-left">
          <p className="text-[38px] font-bold leading-[42px] text-[#333333]">
            {s.value}
          </p>
          {s.label && (
            <p className={ahrefsBodyClass}>{s.label}</p>
          )}
          {s.source && (
            <p className={ahrefsMutedClass}>Source: {s.source}</p>
          )}
        </div>
      ))}
    </div>
  )
}

function ExpertBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { name, title, photo } = attrs
  const initial = name?.charAt(0).toUpperCase() ?? "?"

  return (
    <blockquote className="my-[29px] grid grid-cols-[34px_minmax(0,1fr)] gap-4">
      <Quote className="mt-1 size-7 text-[#d9d9d9]" />
      <div>
        {body && (
          <p className="m-0 text-[18px] italic leading-[29px] text-[#333333]">
            &ldquo;{body}&rdquo;
          </p>
        )}
        <div className="flex items-center gap-3 pt-4">
          {photo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={photo}
              alt={name ?? "Expert"}
              className="size-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#333333]">
              <span className="text-[13px] font-bold text-white">{initial}</span>
            </div>
          )}
          <div className="text-[18px] leading-6 text-[#333333]">
            {name && (
              <span className="font-bold italic">{name}</span>
            )}
            {title && (
              <span className="italic">, {title}</span>
            )}
          </div>
        </div>
      </div>
    </blockquote>
  )
}

function PullquoteBlock({ body }: { body: string }) {
  return (
    <blockquote className="my-[29px] grid grid-cols-[34px_minmax(0,1fr)] gap-4">
      <Quote className="mt-1 size-7 text-[#d9d9d9]" />
      <p className="m-0 text-[18px] italic leading-[29px] text-[#333333]">
        {body}
      </p>
    </blockquote>
  )
}

function FurtherReadingBlock({ body }: { body: string }) {
  const lines = body.split("\n").filter((l) => l.trim())
  return (
    <aside className="my-[29px]">
      <p className="mb-[15px] text-[20px] font-bold leading-6 text-[#333333]">Further reading</p>
      <ul className="list-disc pl-6">
        {lines.map((line, i) => (
          <li key={i} className="pt-0.5 text-[18px] leading-[29px] text-[#333333]">
            <span>{renderInline(line.replace(/^- /, ""), `fr-${i}`)}</span>
          </li>
        ))}
      </ul>
    </aside>
  )
}

function CtaBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const t = useTranslations("blog.header")
  const { title, label } = attrs
  const href = attrs.href ? sanitizeHref(attrs.href) : undefined
  return (
    <aside className="my-10 rounded-md bg-[#dbc2ff] px-6 py-8 text-center text-[#111111] sm:px-8">
      <Sparkles className="mx-auto mb-3 size-5 text-[#6d4fb4]" />
      {title && (
        <p className="text-[26px] font-bold leading-tight">{title}</p>
      )}
      {body && (
        <p className="mx-auto mt-2 max-w-[420px] text-[15px] leading-6 text-[#25212f]">
          {renderInline(body, "cta-body")}
        </p>
      )}
      {href && (
        <a
          href={href}
          className="mt-6 inline-flex min-h-12 items-center justify-center rounded-full bg-[#111a10] px-8 text-[15px] font-bold text-white transition-colors hover:bg-[#1f2d1d]"
        >
          {label ?? t("getStartedFree")}
        </a>
      )}
    </aside>
  )
}

function BylineBlock({ attrs }: { attrs: Record<string, string> }) {
  const { author, reviewer } = attrs
  if (!author && !reviewer) return null

  return (
    <div className="my-6 flex flex-wrap items-center gap-3">
      {author && (
        <div className="flex items-center gap-2 rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-3 py-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#9ca3af]">
            By
          </span>
          <span className="text-[13px] font-semibold text-[#1a1a2e]">{author}</span>
        </div>
      )}
      {reviewer && (
        <div className="flex items-center gap-2 rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-3 py-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#9ca3af]">
            Reviewed by
          </span>
          <span className="text-[13px] font-semibold text-[#1a1a2e]">{reviewer}</span>
        </div>
      )}
    </div>
  )
}

/* ════════════════════════════════════════════════════════════
   B. NEW DIRECTIVES — Group A: Authored ::: fences (18)
   ════════════════════════════════════════════════════════════ */

/* ── 1. warning ── amber callout */
function WarningBlock({ body }: { body: string }) {
  return <EditorialCallout label="Warning" body={body} icon={AlertTriangle} tone="amber" id="warning" />
}

/* ── 2. important ── red callout */
function ImportantBlock({ body }: { body: string }) {
  return <EditorialCallout label="Important" body={body} icon={OctagonAlert} tone="red" id="important" />
}

/* ── 3. definition ── glossary / term definition */
function DefinitionBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { term } = attrs
  return (
    <aside className="my-[26px] bg-[#f8f4ff] py-[30px] pl-[30px] pr-10">
      <div className="mb-[15px] flex items-center gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#efe5ff]">
          <Info className="size-[15px] text-[#6d28d9]" />
        </span>
        <p className="m-0 text-[20px] font-bold leading-6 text-[#333333]">Definition</p>
      </div>
      {term && (
        <p className="mb-[15px] text-[20px] font-bold leading-6 text-[#333333]">{term}</p>
      )}
      {renderSimpleBody(body, "def")}
    </aside>
  )
}

/* ── 4. primer ── "New to X?" beginner box */
function PrimerBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { topic } = attrs
  return (
    <aside className="my-[26px] bg-[#f3f8ff] py-[30px] pl-[30px] pr-10">
      <div className="mb-[15px] flex items-center gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#e2efff]">
          <Lightbulb className="size-[15px] text-[#1d4ed8]" />
        </span>
        <p className="m-0 text-[20px] font-bold leading-6 text-[#333333]">
          {topic ? `New to ${topic}?` : "New to this?"}
        </p>
      </div>
      {renderSimpleBody(body, "primer")}
    </aside>
  )
}

/* ── 6. feature-matrix ── comparison table with yes/no/partial tokens */
function ProsConsEditorialBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { title } = attrs
  const lines = body.split("\n").filter((l) => l.trim())
  const pros = lines.filter((l) => l.startsWith("+ ") || l.startsWith("pro: ")).map((l) => l.replace(/^\+ |^pro: /, ""))
  const cons = lines.filter((l) => l.startsWith("- ") || l.startsWith("con: ")).map((l) => l.replace(/^- |^con: /, ""))

  return (
    <div className="my-9">
      {title && (
        <p className="mb-3 text-[17px] font-bold leading-7 text-[#111111]">{title}</p>
      )}
      <div className="grid border-y border-[#e3e6ea] sm:grid-cols-2">
        <div className="border-b border-[#e3e6ea] py-5 sm:border-b-0 sm:border-r sm:pr-5">
          <p className={labelClass + " text-[#15803d]"}>Pros</p>
          <ul className="space-y-3">
            {pros.map((pro, i) => (
              <li key={i} className="grid grid-cols-[18px_minmax(0,1fr)] gap-3 text-[16px] leading-[26px] text-[#333333]">
                <span className="mt-[1px] shrink-0 font-bold text-[#15803d]">+</span>
                <span>{renderInline(pro, `pro-clean-${i}`)}</span>
              </li>
            ))}
            {pros.length === 0 && (
              <li className="text-[0.875rem] italic text-[#9ca3af]">None listed</li>
            )}
          </ul>
        </div>
        <div className="py-5 sm:pl-5">
          <p className={labelClass + " text-[#b91c1c]"}>Cons</p>
          <ul className="space-y-3">
            {cons.map((con, i) => (
              <li key={i} className="grid grid-cols-[18px_minmax(0,1fr)] gap-3 text-[16px] leading-[26px] text-[#333333]">
                <span className="mt-[1px] shrink-0 font-bold text-[#b91c1c]">-</span>
                <span>{renderInline(con, `con-clean-${i}`)}</span>
              </li>
            ))}
            {cons.length === 0 && (
              <li className="text-[0.875rem] italic text-[#9ca3af]">None listed</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}

function FeatureMatrixBlock({ body }: { body: string }) {
  const lines = body.split("\n").filter((l) => l.trim())
  if (lines.length < 2) return null

  // Parse header row + data rows from pipe-delimited format
  const parseRow = (line: string) =>
    line.split("|").map((c) => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1)

  const headerRow = parseRow(lines[0])
  const dataRows = lines.slice(1).filter((l) => !/^[-|:\s]+$/.test(l)).map(parseRow)

  function CellValue({ value }: { value: string }) {
    const v = value.toLowerCase().trim()
    if (v === "yes" || v === "✓")
      return <span className="font-bold text-[#16a34a]" aria-label="Yes">✓</span>
    if (v === "no" || v === "✗" || v === "x")
      return <span className="font-bold text-[#ef4444]" aria-label="No">✗</span>
    if (v === "partial" || v === "~")
      return <span className="font-bold text-[#d97706]" aria-label="Partial">—</span>
    return <span>{value}</span>
  }

  return (
    <div className="my-[60px] overflow-x-auto">
      <table className={ahrefsTableClass}>
        <thead>
          <tr>
            {headerRow.map((h, i) => (
              <th
                key={i}
                className={`${ahrefsCellClass} text-left font-bold`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={ahrefsCellClass}
                >
                  <CellValue value={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ── 7. decision-table ── if/then decision matrix */
function DecisionTableBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { caption } = attrs
  const lines = body.split("\n").filter((l) => l.trim())

  const parseRow = (line: string) =>
    line.split("|").map((c) => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1)

  const headerRow = parseRow(lines[0] ?? "")
  const dataRows = lines.slice(1).filter((l) => !/^[-|:\s]+$/.test(l)).map(parseRow)

  return (
    <div className="my-[60px]">
      {caption && (
        <p className="mb-[15px] text-[18px] font-bold leading-[29px] text-[#333333]">{caption}</p>
      )}
      <div className="overflow-x-auto">
        <table className={ahrefsTableClass}>
          <thead>
            <tr>
              {headerRow.map((h, i) => (
                <th
                  key={i}
                  className={`${ahrefsCellClass} text-left font-bold`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={ahrefsCellClass}
                  >
                    {renderInline(cell, `dt-${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ── 8. preferred-order ── ranked/ordered recommendation list */
function PreferredOrderBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { title } = attrs
  const lines = body.split("\n").filter((l) => l.trim())

  return (
    <aside className="my-[29px]">
      <p className="mb-[15px] text-[20px] font-bold leading-6 text-[#333333]">
        {title ?? "Recommended order"}
      </p>
      <ol className="list-decimal pl-6">
        {lines.map((line, i) => (
          <li key={i} className="pt-0.5 text-[18px] leading-[29px] text-[#333333]">
            {renderInline(line.replace(/^\d+\.\s*/, ""), `po-${i}`)}
          </li>
        ))}
      </ol>
    </aside>
  )
}

/* ── 9. verdict ── verdict/conclusion box */
function VerdictBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { score, label } = attrs
  const scoreNum = score ? parseFloat(score) : null
  const outOf = attrs["out-of"] ? parseFloat(attrs["out-of"]) : 10

  return (
    <aside className={ahrefsBoxClass}>
      <div className="mb-3 flex items-start justify-between gap-5">
        <p className="text-[20px] font-bold leading-6 text-[#333333]">
          {label ?? "Our verdict"}
        </p>
        {scoreNum !== null && (
          <div className="flex items-baseline gap-0.5">
            <span className="text-[38px] font-bold leading-[42px] text-[#333333]">
              {scoreNum}
            </span>
            <span className={ahrefsMutedClass}>/{outOf}</span>
          </div>
        )}
      </div>
      {renderSimpleBody(body, "verdict")}
    </aside>
  )
}

/* ── 10. badge ── inline badge chip */
const BADGE_CONFIGS: Record<string, { bg: string; text: string; label: string }> = {
  "best-overall": { bg: "#fef9c3", text: "#ca8a04", label: "🏆 Best Overall" },
  "editors-pick": { bg: "#f0f7ff", text: "#1d7df2", label: "✦ Editor's Pick" },
  "best-free": { bg: "#f0fdf4", text: "#16a34a", label: "✓ Best Free" },
}

function BadgeBlock({ attrs }: { attrs: Record<string, string> }) {
  const { type, label: customLabel } = attrs
  const config = BADGE_CONFIGS[type ?? ""] ?? { bg: "#f9fafb", text: "#6b7280", label: "Badge" }
  const cleanLabels: Record<string, string> = {
    "best-overall": "Best overall",
    "editors-pick": "Editor's pick",
    "best-free": "Best free",
  }
  const cleanStyles: Record<string, { bg: string; text: string }> = {
    "best-overall": { bg: "#111111", text: "#ffffff" },
    "editors-pick": { bg: "#eef4ff", text: "#1d4ed8" },
    "best-free": { bg: "#edf8f0", text: "#15803d" },
  }
  const displayLabel = (customLabel ?? cleanLabels[type ?? ""] ?? config.label).replace(/[^\x20-\x7E]/g, "").trim()
  const style = cleanStyles[type ?? ""] ?? { bg: "#f1f2f4", text: "#5f6670" }

  return (
    <span
      className="my-2 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.1em]"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {displayLabel}
    </span>
  )
}

/* ── 11. stat-list ── cited statistics list */
function StatListBlock({ body }: { body: string }) {
  const lines = body.split("\n").filter((l) => l.trim())

  // Each line: "Stat text [Source](url)" or just "Stat text — Source"
  const items = lines.map((line) => {
    const match = line.match(/^(.+?)\s*\[([^\]]+)\]\(([^)]+)\)\s*$/)
    if (match) {
      return { text: match[1].trim(), sourceLabel: match[2], sourceUrl: match[3] }
    }
    const dashMatch = line.match(/^(.+?)\s*[—–-]\s*(.+)$/)
    if (dashMatch) {
      return { text: dashMatch[1].trim(), sourceLabel: dashMatch[2].trim(), sourceUrl: null }
    }
    return { text: line, sourceLabel: null, sourceUrl: null }
  })

  return (
    <aside className="my-[29px]">
      <p className="mb-[15px] text-[20px] font-bold leading-6 text-[#333333]">
        Key statistics
      </p>
      <ul className="list-disc pl-6">
        {items.map((item, i) => (
          <li key={i} className="pt-0.5 text-[18px] leading-[29px] text-[#333333]">
            <span>
              {renderInline(item.text, `sl-${i}`)}
              {item.sourceLabel && (
                <span className="ml-1 text-[16px] leading-[22px] text-[#333333]/70">
                  {item.sourceUrl ? (
                    <>
                      — <a href={sanitizeHref(item.sourceUrl)} className="blog-link" target="_blank" rel="noopener noreferrer">{item.sourceLabel}</a>
                    </>
                  ) : (
                    `— ${item.sourceLabel}`
                  )}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </aside>
  )
}

/* ── 12. tweet ── X/Twitter embed with styled blockquote fallback */
function TweetBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { url, author, handle, date } = attrs

  return (
    <div className="my-[29px]">
      {url ? (
        <blockquote className="pl-[50px]">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#111111]">
              <span className="text-[11px] font-bold text-white">
                {handle ? handle.charAt(0).toUpperCase() : "X"}
              </span>
            </div>
            <div>
              {author && <p className="text-[15px] font-bold leading-5 text-[#111111]">{author}</p>}
              {handle && <p className="text-[13px] leading-5 text-[#7b8087]">@{handle}</p>}
            </div>
            <div className="ml-auto">
              <svg viewBox="0 0 24 24" className="size-5 fill-[#111111]" aria-label="X (Twitter)">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </div>
          </div>
          <p className="m-0 text-[18px] italic leading-[29px] text-[#333333]">
            {body || "Tweet content"}
          </p>
          {date && <p className="mt-3 text-[13px] leading-5 text-[#7b8087]">{date}</p>}
          <a
            href={sanitizeHref(url)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block text-[13px] font-bold text-[#0055ff] hover:underline"
          >
            View on X →
          </a>
        </blockquote>
      ) : (
        <blockquote className="pl-[50px]">
          <p className="m-0 text-[18px] italic leading-[29px] text-[#333333]">{body}</p>
          {author && (
            <footer className="mt-3 text-[13px] leading-5 text-[#7b8087] not-italic">
              — {author}{handle ? ` (@${handle})` : ""}
            </footer>
          )}
        </blockquote>
      )}
    </div>
  )
}

/* ── 13. video ── responsive 16:9 iframe */
function VideoBlock({ attrs }: { attrs: Record<string, string> }) {
  const { src, title, caption } = attrs

  if (!src) {
    return (
      <div className="my-8 flex aspect-video items-center justify-center rounded-lg border border-dashed border-[#e5e7eb] bg-[#f9fafb]">
        <p className="text-[0.875rem] text-[#9ca3af]">Video placeholder (no src)</p>
      </div>
    )
  }

  return (
    <figure className="my-10">
      <div className="relative aspect-video overflow-hidden rounded-lg border border-[#e5e7eb]">
        <iframe
          src={sanitizeHref(src)}
          title={title ?? "Embedded video"}
          className="absolute inset-0 size-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      {caption && (
        <figcaption className="mt-2 text-center text-[0.8125rem] text-[#9ca3af]">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

/* ── 14. faq ── expandable accordion + FAQPage JSON-LD
 *   Body: alternating Q: / A: lines, or ## Question / Answer pairs
 */
function FaqBlock({ body }: { body: string }) {
  const uid = useId()
  const [openIdx, setOpenIdx] = useState<number | null>(0)

  // Parse Q&A pairs
  const pairs: Array<{ q: string; a: string }> = []
  const lines = body.split("\n")
  let i = 0

  while (i < lines.length) {
    const line = lines[i].trim()
    if (line.startsWith("Q:") || line.startsWith("## ")) {
      const q = line.replace(/^Q:\s*|^##\s*/, "").trim()
      const answerLines: string[] = []
      i++
      while (i < lines.length && !lines[i].startsWith("Q:") && !lines[i].startsWith("##")) {
        const al = lines[i].trim()
        if (al.startsWith("A:")) answerLines.push(al.replace(/^A:\s*/, ""))
        else if (al) answerLines.push(al)
        i++
      }
      if (q) pairs.push({ q, a: answerLines.join(" ").trim() })
    } else {
      i++
    }
  }

  return (
    <div className="my-10 border-y border-[#e3e6ea] bg-white">
      <p className="border-b border-[#e3e6ea] py-4 text-[20px] font-bold leading-6 text-[#333333]">
        Frequently asked questions
      </p>
      {pairs.map(({ q, a }, idx) => {
        const isOpen = openIdx === idx
        const btnId = `${uid}-btn-${idx}`
        const panelId = `${uid}-panel-${idx}`
        return (
          <div key={idx} className="border-b border-[#edf0f2] last:border-0">
            <button
              id={btnId}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenIdx(isOpen ? null : idx)}
              className="flex w-full items-start justify-between gap-4 py-5 text-left"
            >
              <span className="text-[22px] font-bold leading-[30px] text-[#333333]">
                {q}
              </span>
              <span
                className="mt-0.5 shrink-0 text-[28px] font-light leading-none text-[#9aa1aa] transition-transform"
                style={{ transform: isOpen ? "rotate(45deg)" : "none" }}
                aria-hidden
              >
                +
              </span>
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={btnId}
              className="pb-5 pt-0"
              hidden={!isOpen}
            >
              <p className={ahrefsBodyClass}>{renderInline(a, `${panelId}-a`)}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ── 15. jumplinks ── in-article jump link nav */
function JumplinksBlock({ body }: { body: string }) {
  const lines = body.split("\n").filter((l) => l.trim())

  // Each line: "Label #anchor-id" or "- [Label](#anchor-id)"
  const links = lines.map((line) => {
    const match = line.match(/\[([^\]]+)\]\(#([^)]+)\)/)
    if (match) return { label: match[1], anchor: match[2] }
    const plainMatch = line.match(/^-?\s*(.+?)\s+#([\w-]+)$/)
    if (plainMatch) return { label: plainMatch[1].replace(/^- /, ""), anchor: plainMatch[2] }
    return null
  }).filter(Boolean) as Array<{ label: string; anchor: string }>

  if (links.length === 0) return null

  return (
    <nav aria-label="Jump to section" className="my-9 border-y border-[#e3e6ea] bg-white py-5">
      <p className={labelClass + " text-[#5f6670]"}>
        Jump to
      </p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {links.map(({ label, anchor }, i) => (
          <li key={i}>
            <a
              href={`#${anchor}`}
              className="group grid grid-cols-[24px_minmax(0,1fr)] gap-2 text-[15px] font-bold leading-6 text-[#333333] hover:text-[#0055ff]"
            >
              <span className="text-[13px] tabular-nums text-[#9aa1aa] group-hover:text-[#0055ff]">
                {String(i + 1).padStart(2, "0")}
              </span>
              {label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/* ── 16. figure ── image + caption + optional source */
function FigureBlock({ attrs }: { attrs: Record<string, string> }) {
  const { src, alt, caption, source, sourceUrl } = attrs

  return (
    <figure className="my-8">
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt ?? caption ?? "Figure"}
          className="w-full border border-[#e3e6ea] object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex aspect-video items-center justify-center border border-dashed border-[#cfd5dc] bg-[#fafafa]">
          <span className="text-[14px] text-[#7b8087]">{alt ?? "Image placeholder"}</span>
        </div>
      )}
      {(caption || source) && (
        <figcaption className="mt-3 text-center text-[13px] leading-5 text-[#7b8087]">
          {caption}
          {source && (
            <>
              {caption ? " · " : ""}
              Source:{" "}
              {sourceUrl ? (
                <a href={sanitizeHref(sourceUrl)} className="blog-link" target="_blank" rel="noopener noreferrer">
                  {source}
                </a>
              ) : (
                source
              )}
            </>
          )}
        </figcaption>
      )}
    </figure>
  )
}

/* ── 17. diagram ── frame + caption (image deferred) */
function DiagramBlock({ attrs }: { attrs: Record<string, string> }) {
  const { caption, src, alt } = attrs

  return (
    <figure className="my-10">
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt ?? caption ?? "Diagram"}
          className="mx-auto w-full max-w-2xl border border-[#e3e6ea]"
          loading="lazy"
        />
      ) : (
        <div className="mx-auto flex aspect-video max-w-2xl items-center justify-center border border-dashed border-[#cfd5dc] bg-[#fafafa]">
          <div className="text-center">
            <span className="block text-[13px] font-bold uppercase tracking-[0.12em] text-[#9aa1aa]">
              Diagram
            </span>
            <span className="mt-2 block text-[14px] text-[#7b8087]">
              {alt ?? caption ?? "Diagram placeholder"}
            </span>
          </div>
        </div>
      )}
      {caption && (
        <figcaption className="mt-3 text-center text-[13px] italic leading-5 text-[#7b8087]">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

/* ── 18. entry ── numbered roundup entry header */
function EntryBlock({ attrs, body }: { attrs: Record<string, string>; body: string }) {
  const { number, name, best_for, price, badge: badgeType, url } = attrs
  const num = number ? parseInt(number, 10) : null

  return (
    <div className="my-10 scroll-mt-24 border-y border-[#e3e6ea] bg-white">
      <div className="flex items-start gap-4 py-5">
        {num !== null && (
          <div className="mt-0.5 shrink-0 text-[13px] font-bold tabular-nums text-[#9aa1aa]">
            {String(num).padStart(2, "0")}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {url ? (
              <a
                href={sanitizeHref(url)}
                className="text-[20px] font-bold leading-7 text-[#111111] hover:text-[#1d7df2]"
                target="_blank"
                rel="noopener noreferrer"
              >
                {name ?? "Entry name"}
              </a>
            ) : (
              <span className="text-[20px] font-bold leading-7 text-[#111111]">
                {name ?? "Entry name"}
              </span>
            )}
            {badgeType && <BadgeBlock attrs={{ type: badgeType }} />}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[13px] leading-5 text-[#6f7378]">
            {best_for && (
              <span>
                <strong className="font-bold text-[#444444]">Best for:</strong> {best_for}
              </span>
            )}
            {price && (
              <span>
                <strong className="font-bold text-[#444444]">Price:</strong> {price}
              </span>
            )}
          </div>
        </div>
      </div>
      {body && (
        <div className="border-t border-[#edf0f2] py-4">
          {renderSimpleBody(body, `entry-${num}`)}
        </div>
      )}
    </div>
  )
}

/* ── Unknown fallback ── */
function UnknownDirectiveBlock({ name, body }: { name: string; body: string }) {
  return (
    <aside className="my-8 rounded-lg border border-dashed border-[#e5e7eb] bg-[#f9fafb] px-5 py-4">
      <p className="mb-1.5 font-mono text-[10px] text-[#9ca3af]">:::{name}</p>
      {body && (
        <p className="text-[0.875rem] text-[#9ca3af]">{body}</p>
      )}
    </aside>
  )
}

/* ════════════════════════════════════════════════════════════
   C. Inline treatment helpers (exported for article-content)
   ════════════════════════════════════════════════════════════ */

/**
 * Renders an inline `code` span as a soft lavender chip.
 * Call this ONLY for inline code, NOT inside <pre> blocks.
 */
export function InlineCodeChip({ children }: { children: ReactNode }) {
  return (
    <code
      className="inline-block rounded px-1.5 py-0.5 font-mono text-[0.8125em] font-medium leading-none"
      style={{ backgroundColor: "#eeedfe", color: "#4338ad" }}
    >
      {children}
    </code>
  )
}

/**
 * Renders a lead paragraph — larger, weight-400, opening sentence style.
 */
export function LeadParagraph({ children }: { children: ReactNode }) {
  return (
    <p
      className="blog-paragraph"
      style={{ fontSize: "1.375rem", fontWeight: 400, lineHeight: 1.55, color: "#333333" }}
    >
      {children}
    </p>
  )
}

/* ── Main directive dispatcher ── */

export function DirectiveBlock({ name, attrs, body }: DirectiveSegment) {
  switch (name) {
    // Existing (14)
    case "tip":
      return <TipBlock body={body} />
    case "note":
      return <NoteBlock body={body} />
    case "sidenote":
      return <SidenoteBlock body={body} />
    case "editor":
      return <EditorBlock body={body} />
    case "nutshell":
      return <NutshellBlock body={body} />
    case "methodology":
      return <MethodologyBlock body={body} />
    case "key-takeaways":
      return <KeyTakeawaysEditorialBlock body={body} />
    case "stat":
      return <StatBlock attrs={attrs} />
    case "stat-group":
      return <StatGroupBlock body={body} />
    case "expert":
      return <ExpertBlock attrs={attrs} body={body} />
    case "pullquote":
      return <PullquoteBlock body={body} />
    case "further-reading":
      return <FurtherReadingBlock body={body} />
    case "cta":
      return <CtaBlock attrs={attrs} body={body} />
    case "byline":
      return <BylineBlock attrs={attrs} />
    // New Group A (18)
    case "warning":
      return <WarningBlock body={body} />
    case "important":
      return <ImportantBlock body={body} />
    case "definition":
      return <DefinitionBlock attrs={attrs} body={body} />
    case "primer":
      return <PrimerBlock attrs={attrs} body={body} />
    case "proscons":
      return <ProsConsEditorialBlock attrs={attrs} body={body} />
    case "feature-matrix":
      return <FeatureMatrixBlock body={body} />
    case "decision-table":
      return <DecisionTableBlock attrs={attrs} body={body} />
    case "preferred-order":
      return <PreferredOrderBlock attrs={attrs} body={body} />
    case "verdict":
      return <VerdictBlock attrs={attrs} body={body} />
    case "badge":
      return <BadgeBlock attrs={attrs} />
    case "stat-list":
      return <StatListBlock body={body} />
    case "tweet":
      return <TweetBlock attrs={attrs} body={body} />
    case "video":
      return <VideoBlock attrs={attrs} />
    case "faq":
      return <FaqBlock body={body} />
    case "jumplinks":
      return <JumplinksBlock body={body} />
    case "figure":
      return <FigureBlock attrs={attrs} />
    case "diagram":
      return <DiagramBlock attrs={attrs} />
    case "entry":
      return <EntryBlock attrs={attrs} body={body} />
    default:
      return <UnknownDirectiveBlock name={name} body={body} />
  }
}
